import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRight, ChevronLeft, CheckCircle2, Clock, Sparkles, Info, HelpCircle, Square, CheckSquare, Target, Lightbulb
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Messages for Stage 2
const messages = {
  fr: {
    instruction: "Tu peux choisir 1 ou 2 réponses par question selon ce qui te correspond.",
    maxReached: "Tu as atteint le maximum de réponses.",
    canSelectMore: "Tu peux encore choisir 1 réponse si tu le souhaites.",
    canSelectTwo: "Tu peux choisir jusqu'à 2 réponses.",
    maxWarning: "Tu peux sélectionner maximum deux réponses.",
    guidance: "Choisis les réponses qui te correspondent le mieux. Si tu hésites, une seule réponse suffit.",
    neutralOption: "Je ne sais pas encore",
  },
  nl: {
    instruction: "Je kunt 1 of 2 antwoorden per vraag kiezen, afhankelijk van wat bij je past.",
    maxReached: "Je hebt het maximum aantal antwoorden bereikt.",
    canSelectMore: "Je kunt nog 1 antwoord kiezen als je wilt.",
    canSelectTwo: "Je kunt tot 2 antwoorden kiezen.",
    maxWarning: "Je kunt maximaal twee antwoorden selecteren.",
    guidance: "Kies de antwoorden die het beste bij je passen. Als je twijfelt, is één antwoord voldoende.",
    neutralOption: "Ik weet het nog niet",
  }
};

// Similar career pairs for the visual indicator
const SIMILAR_CAREER_KEYWORDS = {
  'kinésithérapeute': ['coach sportif', 'préparateur', 'ostéopathe'],
  'coach sportif': ['kinésithérapeute', 'préparateur', 'entraîneur'],
  'psychologue': ['assistant social', 'éducateur', 'conseiller'],
  'architecte': ["architecte d'intérieur", 'designer', 'ingénieur civil'],
  'développeur': ['designer ux', 'designer ui', 'graphiste'],
  'comptable': ['analyste financier', 'auditeur', 'contrôleur'],
  'infirmier': ['aide-soignant', 'auxiliaire'],
};

export default function Stage2Questionnaire() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // { questionId: [{ answerIndex, tags }, ...] }
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showMaxWarning, setShowMaxWarning] = useState(false);
  const [similarCareersDetected, setSimilarCareersDetected] = useState(false);
  
  const msg = messages[language] || messages.fr;
  
  // Get selected careers from navigation state
  const selectedCareers = location.state?.selectedCareers || [];
  const originalResult = location.state?.originalResult;
  
  // Detect if similar careers are selected
  useEffect(() => {
    const detectSimilarCareers = () => {
      const careersLower = selectedCareers.map(c => c.toLowerCase());
      for (const career of careersLower) {
        for (const [key, similars] of Object.entries(SIMILAR_CAREER_KEYWORDS)) {
          if (career.includes(key)) {
            for (const similar of similars) {
              if (careersLower.some(c => c.includes(similar))) {
                return true;
              }
            }
          }
        }
      }
      return false;
    };
    setSimilarCareersDetected(detectSimilarCareers());
  }, [selectedCareers]);
  
  useEffect(() => {
    // Redirect if no careers selected (but only if state is confirmed missing)
    if (!location.state?.selectedCareers || location.state.selectedCareers.length < 2) {
      navigate('/results', { state: { result: originalResult } });
      return;
    }
    
    fetchQuestions();
  }, [language, location.state?.selectedCareers]);
  
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      console.log('Fetching Stage 2 questions for careers:', selectedCareers, 'language:', language);
      
      if (!selectedCareers || selectedCareers.length < 2) {
        console.error('No careers selected, cannot fetch questions');
        setError('No careers selected');
        setLoading(false);
        return;
      }
      
      const response = await axios.post(`${API}/stage2/questions/${language}`, {
        selected_careers: selectedCareers
      });
      
      console.log('Stage 2 questions received:', response.data.questions?.length || 0);
      setQuestions(response.data.questions || []);
      
      // Track Stage 2 started
      const sessionId = localStorage.getItem('ikigai_session_id');
      if (sessionId) {
        try {
          await axios.patch(`${API}/analytics/session/${sessionId}`, {
            stage2_started: true,
            selected_careers_count: selectedCareers.length,
            selected_careers: selectedCareers
          });
          console.log('Stage 2 started tracked for session:', sessionId);
        } catch (trackErr) {
          console.error('Error tracking Stage 2 start:', trackErr);
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching Stage 2 questions:', err);
      setError('Failed to load questions');
      setLoading(false);
    }
  };
  
  // Handle multi-answer selection (1-2 answers allowed)
  const handleAnswerSelect = (questionId, answerIndex, tags) => {
    setShowMaxWarning(false);
    
    setAnswers(prev => {
      const currentAnswers = prev[questionId] || [];
      const existingIndex = currentAnswers.findIndex(a => a.answerIndex === answerIndex);
      
      if (existingIndex !== -1) {
        // Deselect - remove the answer
        const newAnswers = currentAnswers.filter((_, i) => i !== existingIndex);
        return {
          ...prev,
          [questionId]: newAnswers
        };
      } else {
        // Try to select
        if (currentAnswers.length >= 2) {
          // Show warning message
          setShowMaxWarning(true);
          setTimeout(() => setShowMaxWarning(false), 3000);
          return prev;
        }
        // Add new answer
        return {
          ...prev,
          [questionId]: [...currentAnswers, { answerIndex, tags }]
        };
      }
    });
  };
  
  // Handle neutral option selection
  const handleNeutralSelect = (questionId) => {
    setShowMaxWarning(false);
    setAnswers(prev => {
      const currentAnswers = prev[questionId] || [];
      const hasNeutral = currentAnswers.some(a => a.answerIndex === -1);
      
      if (hasNeutral) {
        // Deselect neutral
        return {
          ...prev,
          [questionId]: currentAnswers.filter(a => a.answerIndex !== -1)
        };
      } else {
        // Select neutral (replaces other answers)
        return {
          ...prev,
          [questionId]: [{ answerIndex: -1, tags: [] }]
        };
      }
    });
  };
  
  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setShowMaxWarning(false);
    }
  };
  
  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setShowMaxWarning(false);
    }
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Format answers for API - flatten multi-answers
      const formattedAnswers = [];
      Object.entries(answers).forEach(([questionId, answerList]) => {
        answerList.forEach(answer => {
          if (answer.answerIndex !== -1) { // Skip neutral answers
            formattedAnswers.push({
              question_id: parseInt(questionId),
              answer_index: answer.answerIndex,
              tags: answer.tags || []
            });
          }
        });
      });

      const response = await axios.post(`${API}/stage2/submit`, {
        selected_careers: selectedCareers,
        answers: formattedAnswers,
        original_scores: originalResult?.scores || null,  // Pass Stage 1 scores for bonus calculation
        language
      });

      navigate('/stage2-results', { 
        state: { 
          stage2Result: response.data,
          selectedCareers,
          originalResult,
          sessionId: location.state?.sessionId
        } 
      });
    } catch (err) {
      console.error('Error submitting Stage 2:', err);
      setSubmitting(false);
    }
  };
  
  const currentQuestion = questions[currentIndex];
  const currentAnswers = answers[currentQuestion?.id] || [];
  const selectedCount = currentAnswers.length;
  const isNeutralSelected = currentAnswers.some(a => a.answerIndex === -1);
  const isAnswered = selectedCount > 0;
  const allAnswered = questions.every(q => (answers[q.id]?.length || 0) > 0);
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;
  
  // Get selection indicator message
  const getSelectionMessage = () => {
    if (isNeutralSelected) return null;
    if (selectedCount === 0) return { text: msg.canSelectTwo, color: 'text-[#A4187A]' };
    if (selectedCount === 1) return { text: msg.canSelectMore, color: 'text-[#FB6823]' };
    return { text: msg.maxReached, color: 'text-green-600' };
  };
  
  const selectionMessage = getSelectionMessage();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#EBEBEB] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A4187A]"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-[#EBEBEB] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/results')}
            className="neo-button-primary"
          >
            {t('backHome')}
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#EBEBEB] flex flex-col">
      <Header />
      
      <main className="flex-1 px-4 md:px-8 py-6">
        <div className="max-w-3xl mx-auto">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-6"
          >
            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-2 mb-3">
              <div className="flex items-center gap-1 text-sm font-semibold text-[#FB6823]">
                <span className="w-6 h-6 rounded-full bg-[#FB6823] text-white flex items-center justify-center text-xs">2</span>
                <span>{t('stepIndicator.step2Title')}</span>
              </div>
            </div>
            
            {/* Global Progress Bar - Step 2 starts at 50% */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 h-2 bg-[#E0E0E0] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#A4187A] to-[#FB6823] transition-all duration-300"
                  style={{ width: `${50 + (progress / 100) * 50}%` }}
                />
              </div>
              <span className="text-xs font-medium text-[#FB6823]">{Math.round(50 + (progress / 100) * 50)}%</span>
            </div>
            
            {/* Step Visual Indicators */}
            <div className="flex items-center justify-center gap-4 text-xs text-[#1D1D1D]/60 mb-4">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-[#A4187A]"></div>
                <span className="text-[#A4187A] font-medium">{language === 'fr' ? 'Étape 1 ✓' : 'Stap 1 ✓'}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-[#A4187A]" />
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-[#FB6823]"></div>
                <span className="text-[#FB6823] font-semibold">{language === 'fr' ? 'Étape 2' : 'Stap 2'}</span>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-2 text-[#FB6823] mb-2">
              <Sparkles className="w-5 h-5" />
              <span className="text-sm font-medium">{t('stage2.duration')}</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black text-[#1D1D1D] mb-2">
              {t('stage2.title')}
            </h1>
            <p className="text-[#1D1D1D]/60 text-sm md:text-base">
              {t('stage2.subtitle')}
            </p>
          </motion.div>
          
          {/* Selected Careers Badges */}
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            {selectedCareers.map((career, idx) => (
              <span 
                key={idx}
                className="px-3 py-1 bg-[#F3E5F5] text-[#A4187A] rounded-full text-sm font-medium"
              >
                {career}
              </span>
            ))}
          </div>
          
          {/* Similar Careers Indicator - Visual explanation */}
          {similarCareersDetected && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-r from-[#FB6823]/10 to-[#A4187A]/10 border border-[#FB6823]/30 rounded-xl p-4 mb-6"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FB6823] flex items-center justify-center flex-shrink-0">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Lightbulb className="w-4 h-4 text-[#FB6823]" />
                    <span className="font-bold text-[#A4187A] text-sm">
                      {t('stage2.specificQuestions')}
                    </span>
                  </div>
                  <p className="text-sm text-[#1D1D1D]/70">
                    {t('stage2.similarCareersDetected')} ! {t('stage2.questionsHelp')} :{' '}
                    <span className="font-semibold text-[#A4187A]">
                      {selectedCareers.join(' vs ')}
                    </span>
                  </p>
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-sm text-[#1D1D1D]/60 mb-2">
              <span>{t('stage2.question')} {currentIndex + 1} {t('stage2.of')} {questions.length}</span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {Object.keys(answers).filter(k => answers[k]?.length > 0).length}/{questions.length}
              </span>
            </div>
            <div className="h-2 bg-[#F3E5F5] rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#A4187A] to-[#FB6823]"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
          
          {/* Instruction Banner */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#A4187A]/10 border border-[#A4187A]/20 rounded-xl p-4 mb-4"
          >
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-[#A4187A] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#A4187A] font-medium">
                {msg.instruction}
              </p>
            </div>
          </motion.div>
          
          {/* Question Card */}
          <AnimatePresence mode="wait">
            {currentQuestion && (
              <motion.div
                key={currentQuestion.id}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
                className="neo-card mb-6"
                data-testid={`stage2-question-${currentQuestion.id}`}
              >
                <h2 className="text-lg md:text-xl font-bold text-[#1D1D1D] mb-4">
                  {currentQuestion.question}
                </h2>
                
                {/* Selection Indicator */}
                {selectionMessage && !isNeutralSelected && (
                  <div className={`flex items-center gap-2 mb-4 text-sm ${selectionMessage.color}`}>
                    <div className="flex gap-1">
                      {[0, 1].map(i => (
                        <div
                          key={i}
                          className={`w-3 h-3 rounded-full border-2 ${
                            i < selectedCount
                              ? 'bg-[#FB6823] border-[#FB6823]'
                              : 'border-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="font-medium">{selectionMessage.text}</span>
                  </div>
                )}
                
                {/* Max Warning Toast */}
                <AnimatePresence>
                  {showMaxWarning && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="bg-[#FB6823] text-white text-sm font-medium px-4 py-2 rounded-lg mb-4 flex items-center gap-2"
                    >
                      <Info className="w-4 h-4" />
                      {msg.maxWarning}
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {/* Answer Options - Checkbox Style */}
                <div className="space-y-3">
                  {currentQuestion.answers.map((answer, idx) => {
                    const isSelected = currentAnswers.some(a => a.answerIndex === idx);
                    const isDisabled = !isSelected && selectedCount >= 2 && !isNeutralSelected;
                    
                    return (
                      <button
                        key={idx}
                        onClick={() => handleAnswerSelect(currentQuestion.id, idx, answer.tags)}
                        disabled={isNeutralSelected}
                        className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                          isNeutralSelected
                            ? 'opacity-50 cursor-not-allowed border-gray-200 bg-gray-50'
                            : isSelected
                              ? 'border-[#A4187A] bg-[#A4187A] text-white shadow-lg'
                              : isDisabled
                                ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                                : 'border-[#E0E0E0] bg-white hover:border-[#FB6823] hover:shadow-sm'
                        }`}
                        data-testid={`stage2-answer-${currentQuestion.id}-${idx}`}
                      >
                        <div className="flex items-center gap-3">
                          {/* Checkbox */}
                          <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            isSelected
                              ? 'border-white bg-white'
                              : 'border-[#A4187A]/40'
                          }`}>
                            {isSelected ? (
                              <CheckSquare className="w-5 h-5 text-[#A4187A]" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-300" />
                            )}
                          </div>
                          <span className={`font-medium ${
                            isSelected ? 'text-white' : 'text-[#1D1D1D]'
                          }`}>
                            {answer.text}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                  
                  {/* Neutral Option */}
                  <div className="pt-4 mt-4 border-t border-[#E0E0E0]">
                    <button
                      onClick={() => handleNeutralSelect(currentQuestion.id)}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                        isNeutralSelected
                          ? 'border-[#FB6823] bg-[#FB6823]/10 text-[#FB6823]'
                          : 'border-dashed border-[#E0E0E0] bg-white hover:border-[#FB6823]/50 hover:bg-[#FB6823]/5'
                      }`}
                      data-testid={`stage2-answer-neutral-${currentQuestion.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          isNeutralSelected
                            ? 'border-[#FB6823] bg-[#FB6823]' 
                            : 'border-[#FB6823]/40'
                        }`}>
                          {isNeutralSelected && (
                            <HelpCircle className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <span className={`font-medium ${isNeutralSelected ? 'text-[#FB6823]' : 'text-[#1D1D1D]/60'}`}>
                          {msg.neutralOption}
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
                
                {/* Supportive Guidance */}
                <div className="mt-6 pt-4 border-t border-[#E0E0E0]">
                  <p className="text-xs text-[#1D1D1D]/50 italic flex items-start gap-2">
                    <HelpCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {msg.guidance}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Navigation Buttons */}
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className={`neo-button-outline flex items-center gap-2 ${
                currentIndex === 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              data-testid="stage2-prev-btn"
            >
              <ChevronLeft className="w-5 h-5" />
              {t('stage2.previous')}
            </button>
            
            {currentIndex < questions.length - 1 ? (
              <button
                onClick={handleNext}
                disabled={!isAnswered}
                className={`neo-button-primary flex items-center gap-2 ${
                  !isAnswered ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                data-testid="stage2-next-btn"
              >
                {t('stage2.next')}
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!allAnswered || submitting}
                className={`neo-button-primary flex items-center gap-2 ${
                  (!allAnswered || submitting) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                data-testid="stage2-submit-btn"
              >
                {submitting ? t('loading') : t('stage2.seeResults')}
                <Sparkles className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
